import { create } from 'zustand';
import type { Vehicle, GpsPosition } from '@/lib/api';

// =============================================================================
// Vehicles store — live state vehicle (di-update via WebSocket).
// Berbeda dari TanStack Query cache (yang initial load via REST) — store ini
// jadi single source of truth untuk render real-time di map + popup.
// =============================================================================

interface VehiclesState {
  /** Live state per vehicle, keyed by vh_id. Map untuk fast lookup + iteration. */
  byId: Map<number, Vehicle>;
  /** vh_id yang sedang dipilih user di map / list (untuk highlight + popup). */
  selectedId: number | null;

  /**
   * upsert: insert or update vehicle. Dipakai oleh:
   *   - Initial load dari REST (useVehicles hook).
   *   - WS event 'gps' (data_kind=position atau heartbeat).
   * Field di msg di-merge ke existing entry (preserve field yang tidak di-overwrite).
   */
  upsert: (data: Vehicle | (Partial<Vehicle> & { vh_id?: number; id?: number })) => void;
  /** Bulk upsert — 1 setState untuk N vehicles. Dipakai oleh useVehicleSync
   *  yang batch WS frame setiap 1 detik (hindari re-render storm). */
  upsertMany: (list: Array<Vehicle | (Partial<Vehicle> & { vh_id?: number; id?: number })>) => void;
  /** Update hanya bagian gps (dipakai saat WS 'gps' message masuk). */
  updateGps: (vh_id: number, gps: Partial<GpsPosition>) => void;
  /** Pilih vehicle (mis. saat klik di map atau list). */
  select: (id: number | null) => void;
  /** Bulk replace (mis. saat switch user impersonation → clear & isi ulang). */
  reset: (vehicles: Vehicle[]) => void;
}

// applyPatch — mutate Map in-place dengan deep-merge gps. Dipakai oleh
// upsert (single) + upsertMany (batch). Tidak return — caller pass next Map.
function applyPatch(
  target: Map<number, Vehicle>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Vehicle | (Partial<Vehicle> & { vh_id?: number; id?: number; gps?: any }),
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const id = data.id ?? (data as any).vh_id;
  if (!id) return;
  const existing = target.get(id);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataAny = data as any;
  const mergedGps = dataAny.gps
    ? { ...(existing?.gps ?? {}), ...dataAny.gps }
    : existing?.gps;
  target.set(id, {
    ...existing,
    ...data,
    id,
    ...(mergedGps ? { gps: mergedGps } : {}),
  } as Vehicle);
}

export const useVehiclesStore = create<VehiclesState>((set) => ({
  byId: new Map(),
  selectedId: null,

  upsert: (data) => set((s) => {
    const next = new Map(s.byId);
    applyPatch(next, data);
    return { byId: next };
  }),

  upsertMany: (list) => set((s) => {
    if (!list || list.length === 0) return s;
    const next = new Map(s.byId);
    list.forEach((d) => applyPatch(next, d));
    return { byId: next };
  }),

  updateGps: (vh_id, gpsPatch) => set((s) => {
    const existing = s.byId.get(vh_id);
    if (!existing) {
      // Tidak ada entry — kemungkinan vehicle baru di-add di backend setelah
      // initial load. Skip silent. Caller bisa refetch list kalau perlu.
      return s;
    }
    const next = new Map(s.byId);
    next.set(vh_id, {
      ...existing,
      gps: { ...(existing.gps ?? {} as GpsPosition), ...gpsPatch },
    });
    return { byId: next };
  }),

  select: (id) => set({ selectedId: id }),

  reset: (vehicles) => set(() => {
    const next = new Map<number, Vehicle>();
    vehicles.forEach((v) => next.set(v.id, v));
    return { byId: next, selectedId: null };
  }),
}));
